/* Admin-only navigation layout editor.
 *
 * Drag group tiles to reorder; drag items between/within groups; use
 * per-group role toggles; the "Hidden tools" tray holds removed items so
 * nothing is lost. Save → new versioned layout; Reset → shipped default;
 * Restore → any prior version.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    GripVertical, Plus, Trash2, Eye, EyeOff, Save, Loader2, RotateCcw,
    History, X, Pencil, ChevronRight, EyeIcon,
} from "lucide-react";
import {
    DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, formatApiError } from "@/lib/api";
import { ITEM_CATALOG } from "@/lib/navCatalog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ROLES = [
    { key: "firm_admin", label: "Admin" },
    { key: "firm_staff", label: "Staff" },
];

function newGroupKey() {
    return `custom-${Math.random().toString(36).slice(2, 8)}`;
}

export default function NavLayout() {
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [groups, setGroups] = useState([]);
    const [hidden, setHidden] = useState([]);
    const [overrides, setOverrides] = useState({});
    const [knownItems, setKnownItems] = useState([]);
    const [history, setHistory] = useState([]);
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [undoStack, setUndoStack] = useState([]);
    const [activeId, setActiveId] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    async function load() {
        setLoading(true);
        try {
            const [{ data: layout }, { data: vs }] = await Promise.all([
                api.get("/nav/layout/raw"),
                api.get("/nav/layout/versions"),
            ]);
            setGroups(layout.groups || []);
            setHidden(layout.hidden || []);
            setOverrides(layout.item_overrides || {});
            setKnownItems(layout.known_items || []);
            setHistory(vs || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    // Items referenced somewhere in `groups` are placed; anything else is
    // either in `hidden` or genuinely unplaced (new tool after a deploy).
    const placed = useMemo(() => new Set(groups.flatMap((g) => g.items || [])), [groups]);
    const unplaced = useMemo(() => (knownItems || []).filter((k) => !placed.has(k) && !hidden.includes(k)), [knownItems, placed, hidden]);

    function snapshotForUndo() {
        setUndoStack((s) => [...s, { groups, hidden, overrides }].slice(-10));
    }
    function undo() {
        setUndoStack((s) => {
            if (!s.length) return s;
            const last = s[s.length - 1];
            setGroups(last.groups);
            setHidden(last.hidden);
            setOverrides(last.overrides);
            return s.slice(0, -1);
        });
    }

    /* -------- Mutations -------- */
    function addGroup() {
        snapshotForUndo();
        setGroups([...groups, { id: newGroupKey(), key: newGroupKey(), label: "New group", roles_visible: ["firm_admin", "firm_staff"], items: [], admin_only: false }]);
    }
    function removeGroup(idx) {
        if (!window.confirm("Move all items in this group to the Hidden tools tray?")) return;
        snapshotForUndo();
        const g = groups[idx];
        setHidden([...hidden, ...(g.items || [])]);
        setGroups(groups.filter((_, i) => i !== idx));
    }
    function patchGroup(idx, patch) {
        setGroups(groups.map((g, i) => i === idx ? { ...g, ...patch } : g));
    }
    function toggleGroupRole(idx, role) {
        const g = groups[idx];
        const roles = new Set(g.roles_visible || []);
        if (roles.has(role)) roles.delete(role); else roles.add(role);
        patchGroup(idx, { roles_visible: [...roles] });
    }
    function hideItem(key) {
        snapshotForUndo();
        setGroups(groups.map((g) => ({ ...g, items: (g.items || []).filter((k) => k !== key) })));
        if (!hidden.includes(key)) setHidden([...hidden, key]);
    }
    function unhideToGroup(key, groupIdx) {
        snapshotForUndo();
        setHidden(hidden.filter((k) => k !== key));
        setGroups(groups.map((g, i) => i === groupIdx ? { ...g, items: [...(g.items || []), key] } : g));
    }
    function placeUnplaced(key, groupIdx) {
        snapshotForUndo();
        setGroups(groups.map((g, i) => i === groupIdx ? { ...g, items: [...(g.items || []), key] } : g));
    }

    /* -------- Drag-drop -------- */
    function handleDragStart(event) { setActiveId(String(event.active.id)); }
    function handleDragEnd(event) {
        setActiveId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const aId = String(active.id);
        const oId = String(over.id);
        // Group reorder: `group:<key>` dragged over another `group:<key>`.
        if (aId.startsWith("group:") && oId.startsWith("group:")) {
            const from = groups.findIndex((g) => `group:${g.id}` === aId);
            const to = groups.findIndex((g) => `group:${g.id}` === oId);
            if (from < 0 || to < 0 || from === to) return;
            snapshotForUndo();
            setGroups(arrayMove(groups, from, to));
            return;
        }
        // Item move: active is `item:<key>` and over is either another
        // item or a container id (`drop:<groupId>`).
        if (aId.startsWith("item:")) {
            const key = aId.slice(5);
            let targetGroupIdx = -1;
            let targetItemIdx = -1;
            if (oId.startsWith("drop:")) {
                targetGroupIdx = groups.findIndex((g) => g.id === oId.slice(5));
                targetItemIdx = (groups[targetGroupIdx]?.items || []).length;
            } else if (oId.startsWith("item:")) {
                const overKey = oId.slice(5);
                targetGroupIdx = groups.findIndex((g) => (g.items || []).includes(overKey));
                targetItemIdx = targetGroupIdx >= 0 ? (groups[targetGroupIdx].items || []).indexOf(overKey) : -1;
            }
            if (targetGroupIdx < 0 || targetItemIdx < 0) return;
            snapshotForUndo();
            const next = groups.map((g) => ({ ...g, items: (g.items || []).filter((k) => k !== key) }));
            next[targetGroupIdx] = { ...next[targetGroupIdx], items: [...next[targetGroupIdx].items.slice(0, targetItemIdx), key, ...next[targetGroupIdx].items.slice(targetItemIdx)] };
            setGroups(next);
        }
    }

    /* -------- Save / Reset / Restore -------- */
    async function save() {
        setSaving(true);
        try {
            await api.put("/nav/layout", {
                groups, hidden,
                item_overrides: overrides,
            });
            toast.success("Layout saved — a new version is now live");
            setUndoStack([]);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }
    async function resetDefault() {
        if (!window.confirm("Replace the current layout with the shipped default? A new version is created so you can roll back.")) return;
        try {
            await api.post("/nav/layout/reset", {});
            toast.success("Layout reset to default");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }
    async function restoreVersion(versionId) {
        if (!window.confirm("Restore this version as the current layout?")) return;
        try {
            await api.post(`/nav/layout/restore/${versionId}`, {});
            toast.success("Version restored");
            setVersionsOpen(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    if (loading) return <div className="p-8 text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin" /></div>;

    return (
        <div className="space-y-5" data-testid="nav-layout-page">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-semibold text-[#0F1115]">Navigation layout</h1>
                    <p className="text-[12.5px] text-[#5E636E]">Reorder groups and tools, hide what Staff shouldn't see, and roll back to any prior version. Saves create a new version — nothing is overwritten.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => nav("/app?preview_as=firm_staff")} className="h-9 text-[12.5px]" data-testid="nav-layout-preview">
                        <EyeIcon className="h-3.5 w-3.5 mr-1" /> Preview as Staff
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setVersionsOpen(true)} className="h-9 text-[12.5px]" data-testid="nav-layout-history">
                        <History className="h-3.5 w-3.5 mr-1" /> Version history
                    </Button>
                    <Button variant="outline" size="sm" onClick={undo} disabled={!undoStack.length} className="h-9 text-[12.5px]" data-testid="nav-layout-undo">
                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Undo
                    </Button>
                    <Button variant="outline" size="sm" onClick={resetDefault} className="h-9 text-[12.5px]" data-testid="nav-layout-reset">
                        Reset to default
                    </Button>
                    <Button size="sm" onClick={save} disabled={saving} className="h-9 text-[12.5px] bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="nav-layout-save">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save layout
                    </Button>
                </div>
            </header>

            {unplaced.length > 0 && (
                <div className="rounded-md border border-sky-200 bg-sky-50/60 p-3" data-testid="nav-layout-unplaced">
                    <div className="text-[12px] font-semibold text-sky-900">Unplaced / new tools ({unplaced.length})</div>
                    <div className="text-[11px] text-sky-900/80 mb-2">Added since the last save. Drop them into any group below or add them to a group manually.</div>
                    <ul className="space-y-1.5">
                        {unplaced.map((key) => (
                            <li key={key} className="flex items-center justify-between gap-2 rounded border border-sky-200 bg-white px-2 py-1.5" data-testid={`nav-layout-unplaced-${key}`}>
                                <span className="text-[12.5px] text-[#0F1115]">{ITEM_CATALOG[key]?.label || key}</span>
                                <select onChange={(e) => { const idx = Number(e.target.value); if (idx >= 0) placeUnplaced(key, idx); e.target.value = "-1"; }} defaultValue="-1" className="h-7 rounded border border-sky-300 text-[11.5px] px-1">
                                    <option value="-1">Add to group…</option>
                                    {groups.map((g, i) => <option key={g.id} value={i}>{g.label}</option>)}
                                </select>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <div className="grid gap-3 lg:grid-cols-[1fr_280px]">
                    <div>
                        <SortableContext items={groups.map((g) => `group:${g.id}`)} strategy={verticalListSortingStrategy}>
                            {groups.map((g, idx) => (
                                <GroupCard
                                    key={g.id}
                                    group={g}
                                    idx={idx}
                                    onLabel={(label) => patchGroup(idx, { label })}
                                    onRemove={() => removeGroup(idx)}
                                    onToggleRole={(r) => toggleGroupRole(idx, r)}
                                    onHideItem={hideItem}
                                />
                            ))}
                        </SortableContext>
                        <button type="button" onClick={addGroup} className="mt-3 w-full border border-dashed border-[#E4E4E7] rounded-md py-2 text-[12.5px] text-[#5E636E] hover:bg-zinc-50" data-testid="nav-layout-add-group">
                            <Plus className="h-3.5 w-3.5 inline mr-1" /> Add group
                        </button>
                    </div>
                    <HiddenTray hidden={hidden} groups={groups} onUnhide={unhideToGroup} />
                </div>
                <DragOverlay>
                    {activeId && activeId.startsWith("item:") && (
                        <div className="rounded-md border border-[#0F1115] bg-white px-2.5 py-1.5 text-[12.5px] shadow-lg">
                            {ITEM_CATALOG[activeId.slice(5)]?.label || activeId.slice(5)}
                        </div>
                    )}
                </DragOverlay>
            </DndContext>

            {versionsOpen && <VersionsDialog versions={history} onClose={() => setVersionsOpen(false)} onRestore={restoreVersion} />}
        </div>
    );
}

/* -------- Group card -------- */
function GroupCard({ group, idx, onLabel, onRemove, onToggleRole, onHideItem }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `group:${group.id}`, data: { kind: "group" } });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
    const [editing, setEditing] = useState(false);
    return (
        <div ref={setNodeRef} style={style} className="mb-3 rounded-md border border-[#E4E4E7] bg-white overflow-hidden" data-testid={`nav-layout-group-${group.id}`}>
            <div className="flex items-center gap-2 bg-[#FAFAF7] px-2 py-2 border-b border-[#EEEDE7]">
                <button {...attributes} {...listeners} className="cursor-grab text-[#5E636E] hover:text-[#0F1115]" title="Drag group" data-testid={`nav-layout-group-${group.id}-drag`}>
                    <GripVertical className="h-4 w-4" />
                </button>
                {editing ? (
                    <Input autoFocus value={group.label} onChange={(e) => onLabel(e.target.value)} onBlur={() => setEditing(false)} className="h-7 text-[13px] w-56" />
                ) : (
                    <button className="text-[13px] font-semibold text-[#0F1115]" onClick={() => setEditing(true)} data-testid={`nav-layout-group-${group.id}-label`}>
                        {group.label} <Pencil className="inline h-3 w-3 text-[#5E636E] ml-1" />
                    </button>
                )}
                <div className="flex items-center gap-1 ml-2 text-[11px] text-[#5E636E]">
                    {ROLES.map((r) => {
                        const on = (group.roles_visible || []).includes(r.key);
                        return (
                            <button key={r.key} onClick={() => onToggleRole(r.key)} className={`px-2 py-0.5 rounded border text-[10.5px] font-semibold ${on ? "border-[#0F1115] bg-[#0F1115] text-white" : "border-[#E4E4E7] bg-white text-[#5E636E]"}`} data-testid={`nav-layout-group-${group.id}-role-${r.key}`}>
                                {r.label}
                            </button>
                        );
                    })}
                </div>
                <div className="ml-auto">
                    {group.key !== "overview" && (
                        <button onClick={onRemove} className="text-[#5E636E] hover:text-red-700" title="Delete group" data-testid={`nav-layout-group-${group.id}-remove`}>
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>
            <div className="p-2 min-h-[60px]">
                <GroupDropZone id={`drop:${group.id}`}>
                    <SortableContext items={(group.items || []).map((k) => `item:${k}`)} strategy={verticalListSortingStrategy}>
                        {(group.items || []).length === 0 ? (
                            <div className="text-[11.5px] text-[#5E636E] italic px-2 py-3 text-center">Drop tools here</div>
                        ) : (
                            (group.items || []).map((key) => (
                                <ItemRow key={key} itemKey={key} onHide={() => onHideItem(key)} />
                            ))
                        )}
                    </SortableContext>
                </GroupDropZone>
            </div>
        </div>
    );
}

/* An empty area inside each group that can accept dropped items. */
function GroupDropZone({ id, children }) {
    const { setNodeRef } = useSortable({ id, data: { kind: "dropzone" } });
    return <div ref={setNodeRef} className="min-h-[40px]">{children}</div>;
}

/* -------- Item row (draggable) -------- */
function ItemRow({ itemKey, onHide }) {
    const item = ITEM_CATALOG[itemKey];
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `item:${itemKey}`, data: { kind: "item", itemKey } });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
    if (!item) {
        return (
            <div ref={setNodeRef} style={style} className="flex items-center gap-2 rounded border border-dashed border-amber-300 bg-amber-50 px-2 py-1.5 mb-1 text-[12px]" data-testid={`nav-layout-item-${itemKey}`}>
                <button {...attributes} {...listeners} className="cursor-grab text-amber-700"><GripVertical className="h-3.5 w-3.5" /></button>
                <span className="text-amber-900">{itemKey} <span className="text-[10.5px] opacity-70">(unknown tool key)</span></span>
            </div>
        );
    }
    const Icon = item.icon;
    return (
        <div ref={setNodeRef} style={style} className="flex items-center gap-2 rounded border border-[#EEEDE7] bg-[#FAFAF7] px-2 py-1.5 mb-1 text-[12.5px]" data-testid={`nav-layout-item-${itemKey}`}>
            <button {...attributes} {...listeners} className="cursor-grab text-[#5E636E] hover:text-[#0F1115]" data-testid={`nav-layout-item-${itemKey}-drag`}>
                <GripVertical className="h-3.5 w-3.5" />
            </button>
            <Icon className="h-3.5 w-3.5 text-[#5E636E]" />
            <span className="text-[#0F1115] flex-1">{item.label}</span>
            <button onClick={onHide} className="text-[#5E636E] hover:text-red-700" title="Hide tool" data-testid={`nav-layout-item-${itemKey}-hide`}>
                <EyeOff className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}

/* -------- Hidden tools tray -------- */
function HiddenTray({ hidden, groups, onUnhide }) {
    return (
        <aside className="rounded-md border border-[#E4E4E7] bg-white p-3 h-fit sticky top-4" data-testid="nav-layout-hidden">
            <div className="flex items-center gap-1.5 mb-2">
                <EyeOff className="h-3.5 w-3.5 text-[#5E636E]" />
                <h2 className="text-[12.5px] font-semibold text-[#0F1115]">Hidden tools ({hidden.length})</h2>
            </div>
            <p className="text-[11px] text-[#5E636E] mb-2">Tools in here aren't shown in the sidebar for anyone. Pick a group to send them back.</p>
            {hidden.length === 0 ? (
                <div className="text-[11.5px] text-[#5E636E] italic">Nothing hidden.</div>
            ) : (
                <ul className="space-y-1.5">
                    {hidden.map((key) => (
                        <li key={key} className="rounded border border-[#EEEDE7] bg-[#FAFAF7] px-2 py-1.5 text-[12px]" data-testid={`nav-layout-hidden-${key}`}>
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[#0F1115]">{ITEM_CATALOG[key]?.label || key}</span>
                                <ChevronRight className="h-3 w-3 text-[#5E636E]" />
                            </div>
                            <select
                                onChange={(e) => { const idx = Number(e.target.value); if (idx >= 0) onUnhide(key, idx); e.target.value = "-1"; }}
                                defaultValue="-1"
                                className="mt-1 w-full h-7 rounded border border-[#E4E4E7] text-[11px] px-1"
                                data-testid={`nav-layout-hidden-${key}-restore`}
                            >
                                <option value="-1">Move back to…</option>
                                {groups.map((g, i) => <option key={g.id} value={i}>{g.label}</option>)}
                            </select>
                        </li>
                    ))}
                </ul>
            )}
        </aside>
    );
}

/* -------- Version history dialog -------- */
function VersionsDialog({ versions, onClose, onRestore }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose} data-testid="nav-layout-versions-dialog">
            <div className="w-full max-w-md rounded-md bg-white shadow-xl border border-[#E4E4E7]" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-[#E4E4E7] px-4 py-3">
                    <h2 className="text-[14px] font-semibold text-[#0F1115]">Layout version history</h2>
                    <button onClick={onClose}><X className="h-4 w-4 text-[#5E636E]" /></button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {versions.length === 0 ? <div className="p-4 text-[#5E636E] text-[12.5px]">No prior versions yet.</div> : versions.map((v) => (
                        <div key={v.id} className="px-4 py-3 border-b border-[#F3F3F3] flex items-start justify-between gap-3" data-testid={`nav-layout-version-${v.version}`}>
                            <div>
                                <div className="text-[13px] font-medium text-[#0F1115]">v{v.version} {v.is_current && <span className="ml-1 text-[10.5px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5">current</span>}</div>
                                <div className="text-[11.5px] text-[#5E636E]">{new Date(v.created_at).toLocaleString("en-NZ")}{v.created_by_name ? ` · ${v.created_by_name}` : ""}{v.note ? ` · ${v.note}` : ""}</div>
                            </div>
                            {!v.is_current && (
                                <Button size="sm" variant="outline" onClick={() => onRestore(v.id)} className="h-7 text-[11.5px]" data-testid={`nav-layout-version-${v.version}-restore`}>
                                    Restore
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
